import { DatePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";

import { BasketItem } from "@hermes/api-model-basket";
import {
    Product,
    ProductLeadTime,
    ProductSizeVariant,
} from "@hermes/api-model-product";

import "@angular/localize/init";
import { LOCALE } from "@hermes/app-core";

import { Locale } from "@hermes/locale";

import { getFormattedDate } from "@hermes/utils-generic/helpers";

import {
    SHIPPING_DELAY_DATE,
    SHIPPING_DELAY_GENERIC,
    SHIPPING_DELAY_MINIMUM_DAYS,
    SHIPPING_DELAY_MINIMUM_MONTHS,
    SHIPPING_DELAY_MINIMUM_WEEKS,
    SHIPPING_DELAY_MIN_MAX_DAYS,
    SHIPPING_DELAY_MIN_MAX_MONTHS,
    SHIPPING_DELAY_MIN_MAX_WEEKS,
    SHIPPING_DELAY_WITHIN_DAYS,
    SHIPPING_DELAY_WITHIN_MONTHS,
    SHIPPING_DELAY_WITHIN_WEEKS,
} from "../../constants/lead-time.constants";
import {
    isLeadTimeFrom,
    isLeadTimeFromTo,
    isLeadTimeShippingDate,
    isLeadTimeTo,
} from "../../helpers/lead-time.helper";

@Component({
    selector: "h-lead-time",
    templateUrl: "./product-lead-time.component.html",
    styleUrls: ["./product-lead-time.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductLeadTimeComponent implements OnInit, OnDestroy {
    @Input()
    public showBackground: boolean = false;

    @Input()
    public product$!: Observable<Product | ProductSizeVariant | BasketItem>;

    public subscription: Subscription = new Subscription();
    public leadTimeInformation$: BehaviorSubject<string> =
        new BehaviorSubject<string>("");

    constructor(
        private datePipe: DatePipe,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.product$.subscribe({
                next: (product) => {
                    this.leadTimeInformation$.next(
                        this.buildLeadTimeInformationString(product.leadTime),
                    );
                },
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public processDaysWeeksFormat(
        variableToReplace: string | RegExp,
        currentDelay: string,
        stringDays: string,
        stringWeeks: string,
        stringMonths: string,
        leadTimePeriod: string,
    ) {
        switch (leadTimePeriod) {
            case "days": {
                return stringDays.replace(variableToReplace, currentDelay);
            }
            case "weeks": {
                return stringWeeks.replace(variableToReplace, currentDelay);
            }
            case "months": {
                return stringMonths.replace(variableToReplace, currentDelay);
            }
            default: {
                return "";
            }
        }
    }

    public buildLeadTimeInformationString(
        leadTimeInformationData: ProductLeadTime | undefined,
    ): string {
        if (leadTimeInformationData) {
            if (isLeadTimeShippingDate(leadTimeInformationData)) {
                return SHIPPING_DELAY_DATE.replace(
                    "%s",
                    getFormattedDate(
                        this.locale,
                        this.datePipe,
                    )(leadTimeInformationData.shippingStartDate),
                );
            }
            if (isLeadTimeTo(leadTimeInformationData)) {
                return this.processDaysWeeksFormat(
                    "%s",
                    leadTimeInformationData.to.toString(),
                    SHIPPING_DELAY_WITHIN_DAYS,
                    SHIPPING_DELAY_WITHIN_WEEKS,
                    SHIPPING_DELAY_WITHIN_MONTHS,
                    leadTimeInformationData.period,
                );
            }
            if (isLeadTimeFrom(leadTimeInformationData)) {
                return this.processDaysWeeksFormat(
                    "%s",
                    leadTimeInformationData.from.toString(),
                    SHIPPING_DELAY_MINIMUM_DAYS,
                    SHIPPING_DELAY_MINIMUM_WEEKS,
                    SHIPPING_DELAY_MINIMUM_MONTHS,
                    leadTimeInformationData.period,
                );
            }
            if (isLeadTimeFromTo(leadTimeInformationData)) {
                // Replace first %s with min
                const leadTime: string = this.processDaysWeeksFormat(
                    /%s/,
                    `${leadTimeInformationData.from}`,
                    SHIPPING_DELAY_MIN_MAX_DAYS,
                    SHIPPING_DELAY_MIN_MAX_WEEKS,
                    SHIPPING_DELAY_MIN_MAX_MONTHS,
                    leadTimeInformationData.period,
                );

                // Replace second %s with max
                return this.processDaysWeeksFormat(
                    /%s/,
                    `${leadTimeInformationData.to}`,
                    leadTime,
                    leadTime,
                    leadTime,
                    leadTimeInformationData.period,
                );
            }
        }

        return SHIPPING_DELAY_GENERIC;
    }
}
